import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Check, Plus, X, Shuffle, Heart } from "lucide-react";
import background from "./images/background.png";
import { useAuth } from "./AuthContext";

const API = process.env.REACT_APP_API_URL || "";
const NEW_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;
// Nada agregado antes de este momento cuenta como "Nuevo", sin importar qué
// tan reciente sea su created_at — evita marcar como nuevo todo el catálogo
// que se importó de golpe antes de lanzar este feature.
const NEW_BADGE_LAUNCH = new Date("2026-08-05T20:15:00Z").getTime();

function isRecentlyAdded(createdAt) {
  if (!createdAt) return false;
  const createdMs = new Date(createdAt).getTime();
  if (createdMs < NEW_BADGE_LAUNCH) return false;
  return Date.now() - createdMs < NEW_THRESHOLD_MS;
}

function formatSavedAgo(createdAt) {
  const days = Math.floor(
    (Date.now() - new Date(createdAt).getTime()) / 86400000
  );
  if (days <= 0) return "Guardado hoy";
  if (days === 1) return "Guardado hace 1 día";
  return `Guardado hace ${days} días`;
}

function formatDuration(totalSeconds) {
  if (totalSeconds === null || totalSeconds === undefined) return null;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function extractVideoId(embedHtml) {
  const match = (embedHtml || "").match(/data-video-id="(\d+)"/);
  return match ? match[1] : null;
}

function cleanPreviewCaption(caption) {
  if (!caption) return "";

  let cleaned = caption.replace(/#[^\s#]+/g, "");
  cleaned = cleaned.replace(/\s{2,}/g, " ").trim();
  cleaned = cleaned
    .replace(/(\p{Extended_Pictographic}[\u{FE0F}\u{200D}]?\s*){3,}$/gu, "")
    .trim();

  return cleaned || caption.trim();
}

function stripLeadingEmoji(label) {
  return label
    .replace(/^[\p{Extended_Pictographic}\u{FE0F}\u{200D}]+\s*/u, "")
    .trim();
}

const COPY_TOAST_OPTIONS = {
  position: "bottom-center",
  autoClose: 1800,
  hideProgressBar: true,
  icon: false,
  closeButton: false,
  className:
    "!bg-white !text-[#b06ab3] !text-xs !font-semibold !rounded-full !shadow-md !min-h-0 !py-2 !px-4 !w-fit !mx-auto !mb-6",
  bodyClassName: "!p-0",
};

async function copyTikTokLink(video) {
  try {
    await navigator.clipboard.writeText(video.tiktok_url);
    toast("Link copiado 💜", COPY_TOAST_OPTIONS);
  } catch {
    toast("No se pudo copiar el link", COPY_TOAST_OPTIONS);
  }
}

const TIKTOK_PLAYER_ORIGIN = "https://www.tiktok.com";

function TikTokModal({
  video,
  onClose,
  isFavorited,
  onToggleFavorite,
  onReroll,
}) {
  useEffect(() => {
    if (!video) return;

    const originalOverflow = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [video]);

  if (!video) return null;

  const videoId = extractVideoId(video.embed_html);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-50 p-5 gap-3"
      style={{ touchAction: "none" }}
      onClick={onClose}
    >
      <div
        className="bg-white shadow-lg w-full max-w-md mx-auto relative"
        style={{ borderRadius: "20px", maxHeight: "90vh", overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center font-bold text-lg active:scale-90 transition-transform"
          style={{
            background: "white",
            color: "#2d1f3d",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          }}
          aria-label="Cerrar"
        >
          ×
        </button>
        {onToggleFavorite && (
          <button
            onClick={() => onToggleFavorite(video)}
            className="absolute right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center text-base active:scale-90 transition-transform"
            style={{
              top: "56px",
              background: "rgba(255,255,255,0.45)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            }}
            aria-label={
              isFavorited ? "Quitar de favoritos" : "Guardar en favoritos"
            }
          >
            <Heart
              size={16}
              strokeWidth={2.5}
              color={isFavorited ? "#b06ab3" : "#2d1f3d"}
              fill={isFavorited ? "#b06ab3" : "none"}
            />
          </button>
        )}
        {videoId ? (
          <iframe
            src={`${TIKTOK_PLAYER_ORIGIN}/player/v1/${videoId}?autoplay=0&muted=0&controls=1&rel=0`}
            title={video.caption || "TikTok"}
            allow="autoplay; encrypted-media"
            allowFullScreen
            style={{
              width: "100%",
              maxWidth: "325px",
              height: "580px",
              border: 0,
              display: "block",
              margin: "0 auto",
            }}
          />
        ) : (
          <div className="p-6 text-center">
            <p className="text-sm mb-3" style={{ color: "#9b7eaa" }}>
              No se pudo cargar el video aquí.
            </p>
            <a
              href={video.tiktok_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline"
              style={{ color: "#b06ab3" }}
            >
              Verlo en TikTok
            </a>
          </div>
        )}
      </div>

      {onReroll && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onReroll();
          }}
          className="flex items-center gap-2 py-2.5 px-5 rounded-full text-sm font-bold active:scale-95 transition-transform"
          style={{
            background: "white",
            color: "#5495AA",
            boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
          }}
        >
          🔁 Otro
        </button>
      )}
    </div>
  );
}

function FilterButton({ activeCount, onClick }) {
  const label =
    activeCount === 0
      ? "Filtro: Todos"
      : activeCount === 1
      ? "Filtro: 1 categoría"
      : `Filtro: ${activeCount} categorías`;

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 py-1 text-xs font-bold active:opacity-60 transition-opacity underline"
      style={{
        background: "transparent",
        border: "none",
        color: activeCount > 0 ? "#b06ab3" : "#9b7eaa",
      }}
    >
      {label}
      <ChevronDown size={14} strokeWidth={2.5} />
    </button>
  );
}

function ActiveTagChips({ tags, selectedTags, onRemove }) {
  if (selectedTags.length === 0) return null;

  const selected = tags.filter((tag) => selectedTags.includes(tag.slug));

  return (
    <div
      className="flex gap-2 overflow-x-auto -mx-5 px-5 mb-4"
      style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
    >
      {selected.map((tag) => (
        <button
          key={tag.slug}
          onClick={() => onRemove(tag.slug)}
          className="shrink-0 flex items-center gap-1.5 py-1.5 pl-3 pr-2 rounded-full text-xs font-bold whitespace-nowrap active:scale-95 transition-transform"
          style={{
            background: "rgba(176,106,179,0.15)",
            color: "#b06ab3",
            border: "1.5px solid #b06ab3",
          }}
        >
          {tag.label}
          <X size={13} strokeWidth={3} />
        </button>
      ))}
    </div>
  );
}

function TagFilterSheet({
  open,
  tags,
  tagCounts,
  draftTags,
  matchCount,
  onToggle,
  onReset,
  onApply,
  onClose,
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "rgba(45,31,61,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full font-quicksand rounded-t-3xl overflow-hidden flex flex-col"
        style={{
          background: "#fffaf9",
          maxHeight: "80vh",
          boxShadow: "0 -8px 30px rgba(176,106,179,0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div
            className="w-10 h-1 rounded-full"
            style={{ background: "rgba(176,106,179,0.25)" }}
          />
        </div>

        <div
          className="relative px-6 pt-3 pb-4"
          style={{ borderBottom: "1px solid rgba(176,106,179,0.15)" }}
        >
          <h2 className="text-lg font-extrabold" style={{ color: "#2d1f3d" }}>
            Filtrar por categoría
          </h2>
          <p className="text-xs mt-1" style={{ color: "#9b7eaa" }}>
            Selecciona una o más categorías.
          </p>
          <button
            onClick={onClose}
            className="absolute top-3 right-4 w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: "rgba(176,106,179,0.1)" }}
          >
            <X size={16} color="#b06ab3" />
          </button>
        </div>

        <div className="overflow-y-auto px-2" style={{ flex: 1 }}>
          {tags.map((tag) => {
            const isSelected = draftTags.includes(tag.slug);
            const count = tagCounts[tag.slug] || 0;
            return (
              <button
                key={tag.slug}
                onClick={() => onToggle(tag.slug)}
                className="w-full flex items-center justify-between px-4 py-3.5 text-left active:bg-black/5 transition-colors"
              >
                <span
                  className="text-sm"
                  style={{
                    color: isSelected ? "#2d1f3d" : "#5c4a68",
                    fontWeight: isSelected ? 700 : 500,
                  }}
                >
                  {tag.label}
                </span>
                <span className="flex items-center gap-2.5">
                  <span className="text-xs" style={{ color: "#b09cbd" }}>
                    {count}
                  </span>
                  {isSelected ? (
                    <Check size={18} color="#b06ab3" strokeWidth={3} />
                  ) : (
                    <Plus size={18} color="#d8c9e0" strokeWidth={2.5} />
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <div
          className="flex gap-3 px-5 pt-3"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
          }}
        >
          <button
            onClick={onReset}
            className="flex-1 py-3 rounded-2xl text-sm font-bold active:scale-95 transition-transform"
            style={{
              background: "rgba(176,106,179,0.08)",
              color: "#b06ab3",
            }}
          >
            Limpiar
          </button>
          <button
            onClick={onApply}
            className="flex-1 py-3 rounded-2xl text-sm font-bold text-white active:scale-95 transition-transform"
            style={{ background: "#b06ab3" }}
          >
            Aplicar ({matchCount})
          </button>
        </div>
      </div>
    </div>
  );
}

function VideoCard({ video, onOpen, isFavorited, onToggleFavorite, savedAt }) {
  const previewCaption = cleanPreviewCaption(video.caption);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(video)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen(video);
      }}
      className="w-full text-left active:scale-[0.98] transition-transform overflow-hidden cursor-pointer"
      style={{
        background: "rgba(255,255,255,0.9)",
        borderRadius: "24px",
        boxShadow: "0 4px 24px rgba(176,106,179,0.13)",
        border: "1.5px solid rgba(176,106,179,0.2)",
      }}
    >
      <div className="relative">
        <img
          src={video.thumbnail_url}
          alt={previewCaption || "Momento divertido"}
          loading="lazy"
          style={{
            width: "100%",
            aspectRatio: "4/5",
            objectFit: "cover",
            display: "block",
          }}
        />
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.15)" }}
        >
          <div
            className="rounded-full flex items-center justify-center"
            style={{
              width: "48px",
              height: "48px",
              background: "rgba(255,255,255,0.9)",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#2d1f3d">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
        {isRecentlyAdded(video.created_at) && (
          <span
            className="absolute top-2 left-2 flex items-center gap-1 font-bold rounded-full"
            style={{
              background: "white",
              color: "#b06ab3",
              fontSize: "10px",
              padding: "3px 9px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            }}
          >
            ✨ Nuevo
          </span>
        )}
        {onToggleFavorite && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(video);
            }}
            aria-label={
              isFavorited ? "Quitar de favoritos" : "Guardar en favoritos"
            }
            className="absolute top-2 right-2 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{
              width: "28px",
              height: "28px",
              background: "rgba(255,255,255,0.45)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            }}
          >
            <Heart
              size={14}
              strokeWidth={2.5}
              color={isFavorited ? "#b06ab3" : "#2d1f3d"}
              fill={isFavorited ? "#b06ab3" : "none"}
            />
          </button>
        )}
        {formatDuration(video.duration_seconds) && (
          <span
            className="absolute bottom-2 right-2 text-white font-semibold rounded"
            style={{
              background: "rgba(0,0,0,0.7)",
              fontSize: "11px",
              padding: "2px 6px",
            }}
          >
            {formatDuration(video.duration_seconds)}
          </span>
        )}
      </div>
      <div className="p-3">
        {previewCaption && (
          <p
            className="text-sm font-semibold mb-3 line-clamp-2"
            style={{ color: "#2d1f3d", lineHeight: "1.3" }}
          >
            {previewCaption}
          </p>
        )}
        {savedAt && (
          <p className="text-xs mb-3" style={{ color: "#9b7eaa" }}>
            {formatSavedAgo(savedAt)}
          </p>
        )}
        {video.tags && video.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {video.tags.map((tag) => (
              <span
                key={tag.slug}
                className="text-[10px] font-bold rounded-full"
                style={{
                  background: "rgba(176,106,179,0.15)",
                  color: "#b06ab3",
                  border: "1px solid rgba(176,106,179,0.3)",
                  padding: "2px 8px",
                  lineHeight: "1.4",
                }}
              >
                {stripLeadingEmoji(tag.label)}
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              copyTikTokLink(video);
            }}
            className="flex-1 py-2 rounded-full text-xs font-bold active:scale-95 transition-transform"
            style={{
              background: "white",
              border: "1.5px solid #b06ab3",
              color: "#b06ab3",
            }}
          >
            🔗 <span className="ml-2">Compartir</span>
          </button>
          <a
            href={video.tiktok_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-1 flex items-center justify-center py-2 rounded-full text-xs font-bold active:scale-90 active:brightness-90 transition duration-75"
            style={{ background: "#b06ab3", color: "white" }}
          >
            ▶️ <span className="ml-2">Ver en TikTok</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function FavoritesToggle({ active, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className="flex items-center gap-1.5 py-1.5 px-3 rounded-full text-xs font-bold active:scale-95 active:brightness-90 transition-transform"
      style={
        active
          ? { background: "#b06ab3", color: "white" }
          : { background: "rgba(45,31,61,0.06)", color: "#9b7eaa" }
      }
    >
      <Heart
        size={13}
        strokeWidth={2.5}
        fill={active ? "currentColor" : "none"}
      />
      Favoritos
    </button>
  );
}

function FavoritesLoginPrompt({ onLogin }) {
  return (
    <div
      className="text-center py-8 px-5"
      style={{
        background: "rgba(255,255,255,0.9)",
        borderRadius: "24px",
        boxShadow: "0 4px 24px rgba(176,106,179,0.13)",
        border: "1.5px solid rgba(176,106,179,0.2)",
      }}
    >
      <div
        className="mx-auto mb-3 rounded-full flex items-center justify-center"
        style={{
          width: "44px",
          height: "44px",
          background: "rgba(176,106,179,0.12)",
          fontSize: "19px",
        }}
      >
        🔒
      </div>
      <p className="text-sm font-extrabold mb-1.5" style={{ color: "#2d1f3d" }}>
        Guarda tus joyitas favoritas
      </p>
      <p className="text-xs mb-4" style={{ color: "#9b7eaa" }}>
        Inicia sesión para guardar los momentos que más te encantan y
        encontrarlos aquí cuando quieras.
      </p>
      <button
        onClick={onLogin}
        className="py-2.5 px-6 rounded-full text-sm font-bold text-white active:scale-95 transition-transform"
        style={{ background: "#b06ab3" }}
      >
        Iniciar sesión
      </button>
    </div>
  );
}

function FavoritesEmptyState({ onExplore }) {
  return (
    <div
      className="text-center py-8 px-5"
      style={{
        background: "rgba(255,255,255,0.9)",
        borderRadius: "24px",
        boxShadow: "0 4px 24px rgba(176,106,179,0.13)",
        border: "1.5px solid rgba(176,106,179,0.2)",
      }}
    >
      <p className="text-sm font-extrabold mb-1.5" style={{ color: "#2d1f3d" }}>
        Aún no tienes favoritos, bb 👀
      </p>
      <p className="text-xs mb-4" style={{ color: "#9b7eaa" }}>
        Toca el corazón ❤️ en cualquier video para guardarlo aquí.
      </p>
      <button
        onClick={onExplore}
        className="py-2.5 px-6 rounded-full text-sm font-bold active:scale-95 transition-transform"
        style={{
          background: "white",
          border: "1.5px solid #b06ab3",
          color: "#b06ab3",
        }}
      >
        Explorar joyitas
      </button>
    </div>
  );
}

const FEED_INITIAL = 10;

const MomentosDivertidos = () => {
  const { isLoggedIn, token } = useAuth();
  const navigate = useNavigate();

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isSurpriseModal, setIsSurpriseModal] = useState(false);
  const [showSurpriseFab, setShowSurpriseFab] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowSurpriseFab(window.scrollY > 360);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const [visibleCount, setVisibleCount] = useState(FEED_INITIAL);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draftTags, setDraftTags] = useState([]);

  const [activeTab, setActiveTab] = useState("todos");
  const [favorites, setFavorites] = useState([]); // [{ video_id, created_at }]

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API}/api/video-moments`);
        if (!res.ok) throw new Error("bad response");
        const data = await res.json();
        setVideos(data);
      } catch (err) {
        setError("No se pudieron cargar los momentos");
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  useEffect(() => {
    fetch(`${API}/api/tags`)
      .then((res) => (res.ok ? res.json() : []))
      .then(setTags)
      .catch(() => setTags([]));
  }, []);

  useEffect(() => {
    setVisibleCount(FEED_INITIAL);
  }, [selectedTags, activeTab]);

  useEffect(() => {
    if (!isLoggedIn) {
      setFavorites([]);
      return;
    }
    fetch(`${API}/api/video-favorites`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then(setFavorites)
      .catch(() => setFavorites([]));
  }, [isLoggedIn, token]);

  const favoriteIds = useMemo(
    () => new Set(favorites.map((f) => f.video_id)),
    [favorites]
  );

  const favoritedAtById = useMemo(() => {
    const map = {};
    favorites.forEach((f) => {
      map[f.video_id] = f.created_at;
    });
    return map;
  }, [favorites]);

  const toggleFavorite = async (video) => {
    if (!isLoggedIn) {
      toast("Inicia sesión para guardar favoritos 💜", COPY_TOAST_OPTIONS);
      return;
    }
    const isFav = favoriteIds.has(video.id);
    setFavorites((current) =>
      isFav
        ? current.filter((f) => f.video_id !== video.id)
        : [
            ...current,
            { video_id: video.id, created_at: new Date().toISOString() },
          ]
    );
    try {
      const res = await fetch(`${API}/api/video-favorites/${video.id}`, {
        method: isFav ? "DELETE" : "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("bad response");
    } catch {
      setFavorites((current) =>
        isFav
          ? [
              ...current,
              { video_id: video.id, created_at: new Date().toISOString() },
            ]
          : current.filter((f) => f.video_id !== video.id)
      );
      toast("No se pudo guardar el favorito", COPY_TOAST_OPTIONS);
    }
  };

  const pickSurprise = (pool) => {
    if (pool.length === 0) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  };

  const openFilterSheet = () => {
    setDraftTags(selectedTags);
    setSheetOpen(true);
  };

  const toggleDraftTag = (slug) => {
    setDraftTags((current) =>
      current.includes(slug)
        ? current.filter((s) => s !== slug)
        : [...current, slug]
    );
  };

  const applyFilters = () => {
    setSelectedTags(draftTags);
    setSheetOpen(false);
  };

  const removeTag = (slug) => {
    setSelectedTags((current) => current.filter((s) => s !== slug));
  };

  const tagCounts = useMemo(() => {
    const counts = {};
    videos.forEach((video) => {
      (video.tags || []).forEach((tag) => {
        counts[tag.slug] = (counts[tag.slug] || 0) + 1;
      });
    });
    return counts;
  }, [videos]);

  const tabVideos =
    activeTab === "favoritos"
      ? videos.filter((video) => favoriteIds.has(video.id))
      : videos;

  const filteredVideos =
    selectedTags.length === 0
      ? tabVideos
      : tabVideos.filter((video) =>
          (video.tags || []).some((t) => selectedTags.includes(t.slug))
        );

  const draftMatchCount = useMemo(() => {
    if (draftTags.length === 0) return videos.length;
    return videos.filter((video) =>
      (video.tags || []).some((t) => draftTags.includes(t.slug))
    ).length;
  }, [videos, draftTags]);

  return (
    <div
      className="flex flex-col font-quicksand"
      style={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: "repeat-y",
        backgroundSize: "100% auto",
        backgroundPosition: "top center",
        minHeight: "calc(100vh - 120px)",
      }}
    >
      <div
        className="px-5 pt-6 pb-8 flex flex-col"
        style={{ minHeight: "inherit" }}
      >
        <div className="mb-1">
          {/* <p
            className="text-xs font-bold tracking-widest uppercase"
            style={{ color: "#b06ab3" }}
          >
            Kari-momentos
          </p> */}
          <h1
            className="text-xl font-extrabold mt-0.5"
            style={{ color: "#2d1f3d" }}
          >
            Te traje unas joyitas, bb ✨
          </h1>
          <p className="text-sm mt-1" style={{ color: "#9b7eaa" }}>
            Prepárate para volver a ver uno… y otro… y otro.
          </p>
        </div>

        <div className="flex items-center justify-between mb-3 gap-3 mt-2">
          {tags.length > 0 ? (
            <FilterButton
              activeCount={selectedTags.length}
              onClick={openFilterSheet}
            />
          ) : (
            <span />
          )}
          <FavoritesToggle
            active={activeTab === "favoritos"}
            onClick={() =>
              setActiveTab((current) =>
                current === "favoritos" ? "todos" : "favoritos"
              )
            }
          />
        </div>

        <ActiveTagChips
          tags={tags}
          selectedTags={selectedTags}
          onRemove={removeTag}
        />

        {loading && (
          <div className="flex justify-center py-8">
            <div
              className="animate-spin rounded-full h-7 w-7 border-2"
              style={{ borderColor: "#b06ab3", borderTopColor: "transparent" }}
            />
          </div>
        )}

        {error && (
          <p className="text-center py-8 text-sm" style={{ color: "#c0392b" }}>
            {error}
          </p>
        )}

        {!loading && !error && activeTab === "favoritos" && !isLoggedIn && (
          <FavoritesLoginPrompt onLogin={() => navigate("/login")} />
        )}

        {!loading &&
          !error &&
          activeTab === "favoritos" &&
          isLoggedIn &&
          favorites.length === 0 && (
            <FavoritesEmptyState onExplore={() => setActiveTab("todos")} />
          )}

        {!loading &&
          !error &&
          !(
            activeTab === "favoritos" &&
            (!isLoggedIn || favorites.length === 0)
          ) &&
          videos.length === 0 && (
            <p
              className="text-center py-8 text-sm"
              style={{ color: "#9b7eaa" }}
            >
              Todavía no hay momentos divertidos
            </p>
          )}

        {!loading &&
          !error &&
          !(
            activeTab === "favoritos" &&
            (!isLoggedIn || favorites.length === 0)
          ) &&
          videos.length > 0 &&
          selectedTags.length > 0 &&
          filteredVideos.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm mb-3" style={{ color: "#9b7eaa" }}>
                No hay joyitas con esos tags todavía, bb 👀
              </p>
              <button
                onClick={() => setSelectedTags([])}
                className="text-xs font-bold underline active:opacity-60 transition-opacity"
                style={{ color: "#b06ab3" }}
              >
                Ver todos los momentos
              </button>
            </div>
          )}

        {!(
          activeTab === "favoritos" &&
          (!isLoggedIn || favorites.length === 0)
        ) && (
          <div className="flex flex-col gap-4">
            {filteredVideos.slice(0, visibleCount).map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                onOpen={(v) => {
                  setSelectedVideo(v);
                  setIsSurpriseModal(false);
                }}
                isFavorited={favoriteIds.has(video.id)}
                onToggleFavorite={toggleFavorite}
                savedAt={
                  activeTab === "favoritos" ? favoritedAtById[video.id] : null
                }
              />
            ))}
          </div>
        )}

        {visibleCount < filteredVideos.length && (
          <button
            onClick={() =>
              setVisibleCount((count) =>
                Math.min(count + FEED_INITIAL, filteredVideos.length)
              )
            }
            className="w-full mt-4 py-3 rounded-2xl text-sm font-semibold active:scale-95 transition-transform"
            style={{
              background: "rgba(255,255,255,0.75)",
              color: "#b06ab3",
              border: "1.5px solid rgba(176,106,179,0.3)",
            }}
          >
            Ver más ↓
          </button>
        )}

        {!loading &&
          !error &&
          !(
            activeTab === "favoritos" &&
            (!isLoggedIn || favorites.length === 0)
          ) &&
          filteredVideos.length > 0 &&
          visibleCount >= filteredVideos.length && (
            <div
              className="text-center mt-6 p-6"
              style={{
                background: "rgba(255,255,255,0.9)",
                borderRadius: "24px",
                boxShadow: "0 4px 24px rgba(176,106,179,0.13)",
                border: "1.5px solid rgba(176,106,179,0.2)",
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="flex-1"
                  style={{ height: "1px", background: "rgba(176,106,179,0.3)" }}
                />
                <span style={{ fontSize: "16px" }}>✨</span>
                <div
                  className="flex-1"
                  style={{ height: "1px", background: "rgba(176,106,179,0.3)" }}
                />
              </div>

              <p className="text-sm font-semibold" style={{ color: "#2d1f3d" }}>
                Eso es todo por ahora, bb.
              </p>
              <p className="text-sm my-3" style={{ color: "#9b7eaa" }}>
                💜 Gracias a todos los creadores que nos regalan estas joyitas
              </p>

              <a
                href="/feedback/sugerencia"
                className="text-sm underline"
                style={{ color: "#949494" }}
              >
                ¿Conoces una joyita que debería estar aquí? Recomiéndamela →
              </a>

              <div className="flex items-center gap-3 mt-4">
                <div
                  className="flex-1"
                  style={{ height: "1px", background: "rgba(176,106,179,0.3)" }}
                />
                <span style={{ fontSize: "16px" }}>🩵</span>
                <div
                  className="flex-1"
                  style={{ height: "1px", background: "rgba(176,106,179,0.3)" }}
                />
              </div>
            </div>
          )}
      </div>

      {filteredVideos.length > 0 && !selectedVideo && !sheetOpen && (
        <div
          className="fixed inset-x-0 bottom-0 flex justify-center pointer-events-none"
          style={{ zIndex: 30 }}
        >
          <div
            className="w-full max-w-[500px] flex justify-end px-5"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
            }}
          >
            <div
              className="pointer-events-auto"
              style={{
                opacity: showSurpriseFab ? 1 : 0,
                transform: showSurpriseFab
                  ? "translateY(0) scale(1)"
                  : "translateY(10px) scale(0.85)",
                pointerEvents: showSurpriseFab ? "auto" : "none",
                transition: "opacity 0.25s ease, transform 0.25s ease",
              }}
            >
              <button
                onClick={() => {
                  setSelectedVideo(pickSurprise(filteredVideos));
                  setIsSurpriseModal(true);
                }}
                aria-label="Sorpréndeme"
                className="rounded-full flex items-center justify-center active:scale-[0.82] active:brightness-75 transition duration-200"
                style={{
                  width: "48px",
                  height: "48px",
                  background: "#b06ab3",
                  color: "white",
                  boxShadow:
                    "0 0 0 1.5px rgba(255,255,255,0.9), 0 8px 20px rgba(45,31,61,0.35)",
                }}
              >
                <Shuffle size={19} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      )}

      <TikTokModal
        video={selectedVideo}
        onClose={() => setSelectedVideo(null)}
        isFavorited={!!selectedVideo && favoriteIds.has(selectedVideo.id)}
        onToggleFavorite={toggleFavorite}
        onReroll={
          isSurpriseModal
            ? () => setSelectedVideo(pickSurprise(filteredVideos))
            : undefined
        }
      />

      <TagFilterSheet
        open={sheetOpen}
        tags={tags}
        tagCounts={tagCounts}
        draftTags={draftTags}
        matchCount={draftMatchCount}
        onToggle={toggleDraftTag}
        onReset={() => setDraftTags([])}
        onApply={applyFilters}
        onClose={() => setSheetOpen(false)}
      />
    </div>
  );
};

export default MomentosDivertidos;
